import { Observable, of, throwError } from 'rxjs';

export interface HandleErrorOptions {
  /** HTTP statuses that are an expected answer here, not a fault. Logged at debug level. */
  quiet?: number[];
}

/**
 * `catchError` helper for service calls.
 *
 *   catchError(handleError('listPlans'))            // rethrows: the caller must handle it
 *   catchError(handleError('listPlans', []))        // recovers with the given value
 *
 * WITHOUT a fallback it RETHROWS. It used to return `of(undefined as T)`, which handed the
 * caller a value typed `T` that was not a `T`; every reader then dereferenced it and the
 * screen died somewhere far from the failed request (argus dashboard: `value.customers.nextKey`
 * on a 401). A caller that cannot recover should see the error.
 *
 * WITH a fallback it recovers, because the caller stated what "no answer" means here. Pass one
 * only when an empty list, `null` or `false` is genuinely the right thing to render.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- the overload keeps the caller's <T> readable; the value is never
export function handleError<T = never>(operation?: string): (error: unknown) => Observable<never>;
export function handleError<T>(operation: string, fallback: T, options?: HandleErrorOptions): (error: unknown) => Observable<T>;
export function handleError<T>(operation = 'operation', ...rest: unknown[]): (error: unknown) => Observable<T> {
  const recovers = rest.length > 0;
  const fallback = rest[0] as T;
  const options = (rest[1] ?? {}) as HandleErrorOptions;

  return (error: unknown): Observable<T> => {
    const status = (error as { status?: number } | null)?.status;
    const message = errorMessage(error, 'no reason given');
    if (options.quiet?.includes(status as number)) {
      console.debug(`${operation}: ${status} (expected)`);
    } else {
      console.error(`${operation} failed${status ? ` (${status})` : ''}: ${message}`, error);
    }
    return recovers ? of(fallback) : throwError(() => error);
  };
}

/**
 * Pulls a human-readable reason out of whatever an error callback receives. Use on calls
 * that deliberately skip handleError, where the server's reason is the point.
 *
 * Three shapes, because odin has two error envelopes and the error interceptor flattens errors
 * to a plain string on the way through:
 *   string                   — already flattened by the interceptor
 *   { error: { message } }   — odin OdinErrorAdvice
 *   { errors: [{ reason }] } — odin GlobalExceptionHandler
 */
export function errorMessage(err: any, fallback: string): string {
  if (typeof err === 'string' && err.trim()) return err;
  const body = err?.error ?? err;
  return body?.error?.message
    || body?.errors?.[0]?.reason
    || body?.errors?.[0]?.message
    || body?.message
    || fallback;
}

/**
 * identity 400s carry `errors: [{field, message}]` (ticket §3.3). Returns only the entries
 * that name a field, for mapping onto form controls; use errorMessage() for the rest.
 */
export function fieldErrors(err: any): { field: string, message: string }[] {
  const body = err?.error ?? err;
  const entries: any[] = Array.isArray(body?.errors) ? body.errors : [];
  const identity = entries
    .filter(e => e && typeof e.field === 'string' && e.field)
    .map(e => ({field: e.field, message: e.message || e.reason || 'Invalid value'}));
  // mercury v3 envelope: `error.target` names the field, `error.details[].target` the rest
  const v3 = body?.error;
  const mercury: any[] = v3 ? [v3, ...(Array.isArray(v3.details) ? v3.details : [])] : [];
  const fromMercury = mercury
    .filter(e => e && typeof e.target === 'string' && e.target)
    .map(e => ({field: e.target, message: e.message || 'Invalid value'}));
  return [...identity, ...fromMercury];
}
